import { Box, Typography } from '@mui/material'
import {
  type GridRenderCellParams,
  type GridColDef,
  DataGrid,
} from '@mui/x-data-grid'
import { type components } from 'api/playerPayback/api'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { useGetRewardProgramByCorporateAccountId } from 'hooks/api/useGetRewardProgramByCorporateAccountId'
import { SwitchRewardsProgramActive } from 'components/RewardsProgram/SwitchRewardsProgramActive'
import { useCallback, useState } from 'react'
import { useLayout } from 'hooks/useLayout'
import { ConfirmModal } from 'components/ConfirmModal/ConfirmModal'
import { usePostRewardsProgram } from 'hooks/api/usePostRewardsProgram'
import { useSnackbar } from 'stores/useSnackbar'
import { CellLink } from 'components/CellLink/CellLink'
import { green, grey } from '@mui/material/colors'
import { isEmpty } from 'lodash'
import { CorporateAccountRewardsProgramMobileRow } from './CorporateAccountRewardsProgramMobileRow'
import type { RewardsProgram } from 'types/api'
import { SectionHeader } from 'components/SectionHeader'
import { groupPermissionStore } from 'stores/groupPermissionStore'

const desktopSxDataGrid = {
  '& .MuiDataGrid-row:hover': {
    backgroundColor: 'white',
  },
}

const mobileSxDataGrid = {
  border: 0,
  backgroundColor: grey[50],
  '& .MuiDataGrid-row:hover': {
    backgroundColor: grey[50],
  },
  '& .MuiDataGrid-columnHeaders': {
    border: 0,
  },
  '& .MuiDataGrid-cell': {
    border: 0,
  },
}

const getDesktopColumns = (): GridColDef[] => {
  return [
    {
      field: 'name',
      headerName: 'Program Name',
      flex: 1,
      minWidth: 160,
      renderCell: (params: GridRenderCellParams<RewardsProgram>) =>
        groupPermissionStore.getState().rewards_RewardProgramsPermission ? (
          <CellLink
            text={params.row.name}
            to={`/Rewards/RewardPrograms/${String(params.row.id)}`}
          />
        ) : (
          <Typography>{params.row.name}</Typography>
        ),
    },
    {
      field: 'active',
      headerName: 'Status',
      maxWidth: 230,
      renderCell: (params: GridRenderCellParams<RewardsProgram>) => {
        return (
          <Box
            className="px-2 pt-1 pb-0.5 rounded-sm font-bold uppercase min-w-full min-h-full flex justify-center  items-center"
            sx={{
              backgroundColor: params.row.active ? green[100] : grey[300],
              color: params.row.active ? green[900] : grey[700],
            }}
          >
            {params.row.active ? 'ACTIVE' : 'INACTIVE'}
          </Box>
        )
      },
    },
    {
      field: '__switch__',
      headerName: '',
      flex: 1,
      align: 'right',
      renderCell: (params: GridRenderCellParams<RewardsProgram>) =>
        groupPermissionStore.getState().rewards_RewardProgramsPermission ? (
          <SwitchRewardsProgramActive rewardsProgram={params.row} />
        ) : (
          <Box />
        ),
    },
  ]
}

const getMobileColumns = (): GridColDef[] => [
  {
    field: '__mobile__',
    headerName: '',
    flex: 1,
    align: 'right',
    renderCell: (params: GridRenderCellParams<RewardsProgram>) => (
      <CorporateAccountRewardsProgramMobileRow row={params.row} />
    ),
  },
]

const NoRowsOverlay = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        color: 'text.secondary',
        p: '16px',
        justifyContent: 'center',
        backgroundColor: grey[100],
        height: '110px',
      }}
    >
      <Typography variant="body1">
        No corporate account reward program
      </Typography>
    </Box>
  )
}

interface RewardProgramSectionPropType {
  corporateAccount: components['schemas']['FullCorporateAccountDTO']
}

export const RewardProgramSection = ({
  corporateAccount,
}: RewardProgramSectionPropType) => {
  const { isMobile } = useLayout()
  const setSnackbarMessage = useSnackbar((state) => state.setMessage)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)

  const RewardProgramQuery = useGetRewardProgramByCorporateAccountId({
    corporateAccountId: corporateAccount.id,
  })

  const toggleModalIsOpen = useCallback(() => {
    setIsModalOpen(!isModalOpen)
  }, [isModalOpen, setIsModalOpen])

  const { mutate: postRewardsProgramMutation } = usePostRewardsProgram({
    onSuccess: (_, variables: components['schemas']['RewardsProgramDTO']) => {
      toggleModalIsOpen()
      setSnackbarMessage(`${variables.name} created successfully.`)
    },
    onError: (_, variables: components['schemas']['RewardsProgramDTO']) => {
      setSnackbarMessage(
        `Error creating reward program ${variables.name}.`,
        'error'
      )
    },
  })

  const createRewardsProgram = useCallback(() => {
    postRewardsProgramMutation({
      name: `${corporateAccount.name}`,
      active: true,
      type: 'CorporateAccount',
      corporateAccountId: corporateAccount.id,
    })
  }, [corporateAccount, postRewardsProgramMutation])

  if (RewardProgramQuery.isPending) {
    return <ActivityIndicator sx={{ height: '123px' }} />
  }

  return (
    <Box className="pt-10">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          rowGap: '32px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            rowGap: '16px',
          }}
        >
          {isEmpty(RewardProgramQuery.data) && (
            <SectionHeader
              title="Reward Programs"
              buttonText="Create Reward Program"
              onClickButton={toggleModalIsOpen}
            />
          )}
        </Box>
        <DataGrid
          autoHeight
          columns={isMobile ? getMobileColumns() : getDesktopColumns()}
          rows={RewardProgramQuery.data ?? []}
          getRowHeight={() => (isMobile ? 125 : 'auto')}
          columnHeaderHeight={
            isMobile || isEmpty(RewardProgramQuery.data) ? 0 : 56
          }
          slots={{
            noRowsOverlay: NoRowsOverlay,
          }}
          disableColumnFilter
          disableColumnMenu
          disableColumnSelector
          disableDensitySelector
          disableRowSelectionOnClick
          hideFooter
          sx={
            isMobile || isEmpty(RewardProgramQuery.data)
              ? mobileSxDataGrid
              : desktopSxDataGrid
          }
        />
      </Box>
      <ConfirmModal
        confirmMessage={`Please confirm you want to create the following rewards program for ${corporateAccount.name}`}
        secondaryMessage={`${corporateAccount.name}`}
        isModalOpen={isModalOpen}
        toggleIsOpen={toggleModalIsOpen}
        onConfirm={createRewardsProgram}
        onCancel={toggleModalIsOpen}
      />
    </Box>
  )
}
